import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import { OdooContext } from "OdooProvider";
import OdooCustomersView from "pages/retailer/retailers/OdooCustomersView";
import { useContext } from "react";
import { FaLink } from "react-icons/fa6";
import { addressKeyMap, customerKeyMap } from "./config";
import ChooseAddressView from "./odoo/ChooseAddressView";
import { ODOO_SAVE_CUSTOMER } from "./odoo/graphql";

const CustomerView = ({ order, source }) => {
  const { odooUrlInc } = useContext(OdooContext);
  const modal = useModals();

  const [saveCustomer, saveCustomerRes] = useMutation(ODOO_SAVE_CUSTOMER, {
    onCompleted() {
      Alert("success", "Customer connected successfully");
    },
    onError(error) {
      window.alert(parseError(error));
    },
    refetchQueries: ["FETCH_INC_EXTERNAL_ORDER_CUSTOMERS"],
    awaitRefetchQueries: true,
  });
  const [saveAddress, saveAddressRes] = useMutation(ODOO_SAVE_CUSTOMER, {
    onCompleted() {
      Alert("success", "Address connected successfully");
    },
    onError(error) {
      window.alert(parseError(error));
    },
    refetchQueries: ["FETCH_INC_EXTERNAL_ORDER_CUSTOMERS"],
    awaitRefetchQueries: true,
  });

  function onSelectHandler(customer) {
    if (window.confirm("Are you sure you want to connect this customer?")) {
      modal.hide();

      const variables = {
        instance: "INC",
        id: customer.id,
      };

      const key = customerKeyMap[source + "Camel"];
      variables[key] = order.contact.externalId;

      saveCustomer({ variables });
    }
  }

  function tryConnectCustomer() {
    modal.present({
      title: "Connect Odoo Customer",
      subtitle: `External Customer ID: #${order.contact.externalId}`,
      children: (
        <OdooCustomersView
          instance="INC"
          useType="LINK_CUSTOMER"
          onSelect={onSelectHandler}
          initialSearchText={order.contact.name || ""}
        />
      ),
    });
  }

  function tryConnectDeliveryAddress() {
    if (!order.odooCustomer) {
      window.alert("Please connect the customer first");
      return;
    }

    if (order.odooCustomer.child_ids.length === 0) {
      window.alert(
        "No delivery address found for this customer in Odoo. Please add a delivery address in Odoo first.",
      );
      return;
    }

    const addressOptions = order.odooCustomer.child_ids.filter(
      (address) => address.type === "delivery",
    );

    modal.present({
      title: "Connect Delivery Address",
      subtitle: `${source.toUpperCase()} Location ID: #${order.shipTo.externalId}`,
      children: (
        <ChooseAddressView
          order={order}
          hide={modal.hide}
          options={addressOptions}
          onSelect={onSelectAddressHandler}
        />
      ),
    });
  }

  function onSelectAddressHandler(address) {
    if (window.confirm("Are you sure you want to connect this address?")) {
      modal.hide();

      const variables = {
        instance: "INC",
        id: address.id,
      };

      const addressKey = addressKeyMap[source + "Camel"];
      if (addressKey) {
        variables[addressKey] = order.shipTo.externalId;
      }

      saveAddress({ variables });
    }
  }

  return (
    <div className="space-y-1 whitespace-nowrap">
      <div className="flex items-center space-x-2">
        <div>
          <span className={!order.odooCustomer ? "text-red-500" : ""}>
            #{order.contact.externalId}. {order.contact.name}
          </span>
        </div>
        <Button
          inline
          loading={saveCustomerRes.loading}
          onClick={tryConnectCustomer}
        >
          <FaLink />
        </Button>

        {order.odooCustomer && (
          <a
            className="text-purple-600"
            href={`${odooUrlInc}/odoo/customers/${order.odooCustomer.id}`}
            target="_blank"
          >
            {order.odooCustomer.name}(View in Odoo)
          </a>
        )}
      </div>

      {order.externalOdooCustomers?.length > 1 && (
        <div className="text-red-600 text-xs whitespace-pre-wrap">
          <p>
            ⚠️ Multiple Odoo Customers found for the customer(ID:
            {order.contact.externalId}), Odoo Customers:{" "}
            <span className="space-x-2">
              {order.externalOdooCustomers.map((c) => (
                <a
                  className="text-purple-600"
                  href={`${odooUrlInc}/odoo/customers/${c.id}`}
                  target="_blank"
                >
                  {c.name}
                </a>
              ))}
            </span>
            .
          </p>
          <p>Please connect the only correct one and refresh this page.</p>
        </div>
      )}

      <div className={`whitespace-normal align-middle`}>
        <div
          className={`flex items-center space-x-2 ${!order.odooDeliveryAddress ? "text-red-500" : ""}`}
        >
          <span>
            🚚 Ship to: #{order.shipTo.externalId}.{order.shipTo.name}
          </span>
          <span>
            <Button
              inline
              onClick={tryConnectDeliveryAddress}
              loading={saveAddressRes.loading}
            >
              <FaLink />
            </Button>
          </span>
        </div>
        <div className="mt-1">
          📍 {order.shipTo.line1},
          {order.shipTo.line2 ? ` ${order.shipTo.line2},` : ""}{" "}
          {order.shipTo.city}, {order.shipTo.state} {order.shipTo.postalCode}
        </div>
      </div>
    </div>
  );
};

export default CustomerView;
